import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
import { unitSchema } from '../catalog/unit.js';
const idRef = z.string().regex(/^\d+$/, 'Identifikator raqamlardan iborat bo\'lishi kerak');
/**
 * Miqdor — `numeric(14,3)`. Kasr mumkinligi BIRLIKKA bog'liq (`DONA` da yo'q),
 * lekin bu tekshiruv sxemada emas: birlik nomenklaturadan o'qiladi, so'rovda
 * kelmaydi. Shu sababli sxema faqat sonning o'zini qo'riqlaydi, birlik qoidasi
 * esa xizmatda (`assertQtyForUnit`) — u yerda xato matni ham aniq bo'ladi.
 */
const qtySchema = z
    .number()
    .positive('Miqdor musbat bo\'lishi kerak')
    .max(99_999_999_999, 'Miqdor juda katta');
/**
 * Bir kirim hujjatidagi satr chegarasi.
 *
 * Har satr o'z partiyasini, joylashuvini va harakat yozuvini tug'diradi —
 * ya'ni 1000 satr bitta tranzaksiyada ~4000 yozuv. Undan kattasi Excel
 * importi orqali bo'lib yuboriladi (`POST /receipts/import`).
 */
export const MAX_RECEIPT_LINES = 1000;
export const receiptLineInputSchema = z.object({
    nomenclatureId: idRef,
    qty: qtySchema,
    cellId: idRef,
    note: z.string().max(500).optional(),
});
export const createReceiptSchema = z.object({
    warehouseId: idRef,
    /** MTU aktining raqami — kirimning tashqi asosi (bo'lmasligi ham mumkin). */
    mtuAktNo: z.string().max(60).optional(),
    supplier: z.string().max(200).optional(),
    receivedAt: z.coerce.date(),
    note: z.string().max(500).optional(),
    lines: z.array(receiptLineInputSchema).min(1, 'Kirimda kamida bitta satr bo\'lishi kerak').max(MAX_RECEIPT_LINES),
});
export const receiptQuerySchema = paginationQuerySchema.extend({
    warehouseId: idRef.optional(),
    search: z.string().max(200).optional(),
});
export const moveStockSchema = z.object({
    batchId: idRef,
    fromCellId: idRef,
    toCellId: idRef,
    qty: qtySchema,
    /**
     * NEGA ko'chirilyapti — MAJBURIY.
     *
     * Ilgari ixtiyoriy edi va amalda hech kim yozmasdi. Ko'chirish esa
     * tarixda eng chalkash harakat: mahsulot bir katakdan yo'qolib,
     * boshqasida paydo bo'ladi va oradan bir oy o'tgach "buni kim, nega
     * qildi?" degan savolga javob qolmaydi. Inventarizatsiyada aynan shu
     * savol beriladi.
     *
     * Uch belgi — eng past chegara: "ha", "ok" kabi javoblar sababni
     * yozganday ko'rinib, aslida hech nima aytmaydi.
     */
    reason: z.string().trim().min(3, 'Ko\'chirish sababini yozing').max(500),
});
/**
 * Qoldiq qidiruvi. `onlyAvailable` — band qilinganini chiqarib tashlaydi:
 * buyurtmachi talabnoma yozayotganda "bor" deb ko'rgan narsasi haqiqatda
 * boshqa talabnomaga band bo'lib turmasligi kerak.
 *
 * `onlyAvailable` ATAYLAB `boolean` ga o'girilmaydi (`.transform` yo'q):
 * `ZodValidationPipe` sxemaning kirish va chiqish turlari BIR XIL bo'lishini
 * talab qiladi. O'girish kontrollerda, bir qatorda bajariladi —
 * `isOnlyAvailable`.
 */
export const stockSearchQuerySchema = paginationQuerySchema.extend({
    q: z.string().max(200).optional(),
    mtuId: idRef.optional(),
    warehouseId: idRef.optional(),
    /**
     * BITTA nomenklatura bo'yicha aniq filtr — `q` ning o'rniga emas, YONIDA.
     *
     * Kerak bo'lgan joyi: skladchi talabnomani qabul qilayotganda har satr
     * uchun "shu mahsulot qaysi partiyada, qaysi katakda va qancha bor"
     * degan savolga javob kerak. `q` matn bo'yicha o'xshatib qidiradi va
     * kodning bir qismi boshqa mahsulotda ham uchrasa, ekranga begona
     * qatorlar chiqib qolardi.
     */
    nomenclatureId: idRef.optional(),
    onlyAvailable: z.enum(['true', 'false']).optional(),
});
/** `?onlyAvailable=true` — boshqa har qanday qiymat "hammasi" degani. */
export const isOnlyAvailable = (raw) => raw === 'true';
/**
 * Excel import chegaralari.
 *
 * Shartnomada turadi, chunki uni IKKI tomon biladi: frontend faylni
 * yubormasdan oldin o'lchamini tekshiradi va odam 40 MB li faylni yuklab,
 * xatoni faqat kutgandan keyin ko'rmaydi.
 */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/**
 * `.xlsx` MIME turi. `mimetype` ni MIJOZ yozadi, ya'ni bu ro'yxat o'zi
 * yetarli emas — server magic baytlarni ham tekshiradi (`50 4B 03 04`).
 * Ikkinchi qiymat — ba'zi mijozlar (va `curl`) yuboradigan umumiy tur.
 */
export const XLSX_MIME_TYPES = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
];
/** Bitta faylda ko'pi bilan shuncha satr — undan kattasi bo'lib yuboriladi. */
export const MAX_IMPORT_ROWS = 20_000;
/**
 * Import bo'lakning kattaligi. Har bo'lak O'Z tranzaksiyasi va O'Z hujjati
 * bo'ladi: 20 000 satrni bitta tranzaksiyada yozish satrlarni daqiqalab
 * qulflab turardi.
 */
export const IMPORT_CHUNK_ROWS = 500;
/**
 * Import so'rovi — `multipart/form-data` ning MATN maydonlari.
 *
 * `lines` yo'q: satrlar faylning ichida keladi. `strict` esa so'rov
 * parametri (`?strict=true`), tanada emas — u faylning MAZMUNIGA emas,
 * IMPORT REJIMIGA tegishli.
 */
export const importReceiptSchema = z.object({
    warehouseId: idRef,
    mtuAktNo: z.string().max(60).optional(),
    supplier: z.string().max(200).optional(),
    receivedAt: z.coerce.date(),
    note: z.string().max(500).optional(),
});
export const importOptionsSchema = z.object({
    /** `true` bo'lsa bironta xato satr butun importni bekor qiladi. */
    strict: z.enum(['true', 'false']).optional(),
});
export const isStrictImport = (raw) => raw === 'true';
export const receiptSchema = z.object({
    id: z.string(),
    docNo: z.string(),
    warehouseId: z.string(),
    /** QO'LDA kiritiladigan MTU akti raqami. Tushumdan yozilgan kirimda bo'sh. */
    mtuAktNo: z.string().nullable(),
    /**
     * Qaysi tushum hisobotidan tug'ilgan.
     *
     * Haqiqiy havola — hisobot raqami `mtuAktNo` ga yozilmaydi: u qidiruvda
     * ishlardi, lekin tizim bog'lanishni bilmasdi va hujjatdan hujjatga
     * o'tib bo'lmasdi.
     */
    supplyReportId: z.string().nullable(),
    supplier: z.string().nullable(),
    receivedAt: z.string(),
    note: z.string().nullable(),
    createdBy: z.string().nullable(),
    /*
     * BEKOR QILINGAN kirim ham qoladi — o'chirilmaydi.
     *
     * `movement` jurnali bazada o'zgarmas (UPDATE/DELETE trigger bilan
     * taqiqlangan), shuning uchun "xato kirimni o'chirish" degan amal umuman
     * yo'q: uning o'rniga teskari `ADJUST` harakati yoziladi va hujjat shu uch
     * maydon bilan belgilanadi. Qog'ozda iz qoladi, qoldiq esa to'g'rilanadi.
     */
    cancelledAt: z.string().nullable(),
    cancelledBy: z.string().nullable(),
    cancelReason: z.string().nullable(),
});
/** Bekor qilish SABABSIZ bo'lmaydi — buxgalter keyin nega ekanini so'raydi. */
export const cancelReceiptSchema = z.object({
    reason: z.string().min(3, 'Bekor qilish sababi yozilishi shart').max(500),
});
export const receiptLineSchema = z.object({
    id: z.string(),
    lineNo: z.number().int(),
    nomenclatureId: z.string(),
    batchId: z.string(),
    batchNo: z.string(),
    barcode: z.string(),
    cellId: z.string(),
    qty: z.number(),
    note: z.string().nullable(),
});
export const receiptWithLinesSchema = receiptSchema.extend({ lines: z.array(receiptLineSchema) });
export const stockPlaceSchema = z.object({
    /*
     * Nomenklatura joylashuvning O'ZIDA turadi, guruh sarlavhasida emas.
     *
     * `GET /stock/search` da u takrorlanadi (guruh ham, satr ham biladi) va bu
     * ataylab: qidiruvdan tashqari IKKI chaqiruvchi joylashuvni guruhsiz oladi
     * — katak kartochkasi (`by-cell`) va shtrix-kod skaneri (`by-batch`).
     * Ularda "bu nima?" degan savolga javob bo'lmasa, ekranda mahsulot nomisiz
     * partiya raqami qolardi.
     */
    nomenclatureId: z.string(),
    nomenclatureCode: z.string(),
    nomenclatureName: z.string(),
    unit: unitSchema,
    batchId: z.string(),
    batchNo: z.string(),
    warehouseId: z.string(),
    warehouseName: z.string(),
    cellId: z.string(),
    cellCode: z.string(),
    qty: z.number(),
    reservedQty: z.number(),
    availableQty: z.number(),
    /**
     * Etiketka oxirgi marta qachon bosilgan. `null` — hech qachon.
     *
     * Ekranda aynan shu savolga javob beriladi: qutida etiketka bormi.
     * Bosilgan FAKTI qayd qilinadi, yopishtirilgani emas — buni tizim
     * bilolmaydi.
     */
    labelPrintedAt: z.string().nullable(),
});
export const stockGroupSchema = z.object({
    nomenclatureId: z.string(),
    code: z.string(),
    name: z.string(),
    unit: unitSchema,
    totalQty: z.number(),
    totalAvailableQty: z.number(),
    places: z.array(stockPlaceSchema),
});
/**
 * Idempotentlik kaliti sarlavhasi.
 *
 * Shartnomada turadi, chunki uni IKKI tomon biladi: frontend har "Kirim
 * qilish" bosishida bitta UUID yuboradi va tarmoq uzilib qayta yuborilsa
 * ham hujjat ikkilanmaydi.
 */
export const IDEMPOTENCY_HEADER = 'idempotency-key';
export const idempotencyKeySchema = z
    .string()
    .min(8, 'Idempotency-Key kamida 8 belgidan iborat bo\'lishi kerak')
    .max(200)
    .regex(/^[A-Za-z0-9._:-]+$/, 'Idempotency-Key da faqat harf, raqam va `._:-` bo\'lishi mumkin');
//# sourceMappingURL=stock.schema.js.map